import {Amplify} from 'aws-amplify';
import type {ResourcesConfig} from 'aws-amplify';

/**
 * Fill these in from your Cognito User Pool / Identity Pool setup.
 * Values below are read from Vite env vars so they can differ per
 * environment (.env.development, .env.production, etc).
 */
export function configureAmplify() {
   const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '';
   const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
   const identityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
   const s3Bucket = import.meta.env.VITE_S3_BUCKET_NAME;
   const s3Region = import.meta.env.VITE_S3_REGION;

   // Amplify's Auth.Cognito type is a strict union keyed on whether
   // identityPoolId is present (it disallows mixing the two shapes),
   // which TS can't narrow through a runtime ternary — build each
   // branch as its own fully-typed config and configure directly.
   const config: ResourcesConfig = identityPoolId
      ? {Auth: {Cognito: {userPoolId, userPoolClientId, identityPoolId, loginWith: {username: true, email: true}}}}
      : {Auth: {Cognito: {userPoolId, userPoolClientId, loginWith: {username: true, email: true}}}};

   // Run ticket photo upload (docs/run-ticket-scan-spec.md) — only
   // configured when both env vars are present. Blank in
   // .env.production today since that bucket/IAM isn't provisioned yet
   // (Duane confirmed 2026-08-19: the test bucket is fully set up, prod
   // isn't needed yet). Leaving Storage unconfigured there means
   // lib/runTicketPhotoUpload.ts's calls fail fast with Amplify's own
   // "no Storage configuration" error instead of silently pointing at
   // the wrong bucket/region.
   if (s3Bucket && s3Region) {
      config.Storage = {S3: {bucket: s3Bucket, region: s3Region}};
   }

   Amplify.configure(config);
}

/**
 * The S3 bucket name Storage was configured with (VITE_S3_BUCKET_NAME) —
 * exported 2026-08-22 so lib/runTicketPhotoUpload.ts can prepend it to
 * the value stored/sent as ticketImageS3Data.imageLocation (the backend
 * has no Amplify config of its own to tell it which bucket a key lives
 * in) and strip it back off when going the other direction. Amplify's
 * own uploadData/remove calls never see this value directly — they're
 * already scoped to this bucket via Storage.S3.bucket above, so
 * prepending it to their `path` would create a literal subfolder named
 * after the bucket INSIDE the bucket, not select a bucket.
 */
export function getS3BucketName(): string | undefined {
   return import.meta.env.VITE_S3_BUCKET_NAME;
}
